/* ============================================================
   TDHQ quick add - tdhq-quickadd.js

   Adds a product to the basket straight from a product card.

   WHY: every competitor checked puts this on the card. MyCarCleaning's card
   carries "In stock", "Add to cart" and "Quick view"; TDHQ's card ended at
   "View Details", so buying two known items meant two full page journeys.

   PROGRESSIVE ENHANCEMENT, and it matters here more than usual because this
   touches the basket:
     - the card is a REAL form whose button posts to /cart/add (formaction),
       so with no JavaScript, before this file has run, or if it fails to
       load, the button still adds the item and Shopify redirects to the cart.
       The form itself has no action (U.58): Shopify's analytics script reads
       every form whose action is /cart/add at page load, logging a "viewed"
       variant per card and erroring on cards outside the page's product list.
     - this script takes the click over and posts the card's
       data-tdhq-variant-id to /cart/add.js, after checking the stock left
       (tdhq-qty.js knows the stock and what the basket already holds).
     - if that request fails for ANY reason the form is submitted natively
       rather than showing an error. A shopper who clicks "Add" must never end
       up with nothing in the basket and no explanation.

   The basket count and total are refreshed by re-rendering the HEADER SECTION
   through the Section Rendering API and copying the two values across. That is
   deliberate: formatting money in JavaScript means reimplementing the shop's
   currency format, which drifts. Letting Liquid render it cannot drift.
   ============================================================ */
(function () {
  'use strict';

  var BUSY = 'is-adding';
  var DONE_MS = 1800;

  /* ONE ADD AT A TIME PER STOCK-TRACKED ITEM, until the basket is known
     again. The add answers before the basket refresh lands, and until then
     the stock check reads the old basket - so a quick second click, or a
     second card for the same item, could pass it twice. */
  var pending = {};
  document.addEventListener('tdhq:cart:changed', function () { pending = {}; });

  /* Refresh the header basket count.

     The first version of this asked the Section Rendering API to re-render the
     header section so Liquid could format everything. That does not work, and
     the reason is a hard platform limit worth recording: the header lives in a
     SECTION GROUP (rendered by `{% sections 'header-group' %}`), and the
     Section Rendering API cannot render section-group sections. Requesting it
     returned the page's own main section instead — `{"collection": …}` — so the
     badge silently never updated.

     /cart.js is the right source anyway. The only value the badge shows is an
     INTEGER, so there is no currency to format and nothing that can drift.
     When the "show basket value" setting is on there is a money string too.
     That is formatted with the drawer's published formatter - the shop's own
     money_format, one implementation for the whole theme - rather than by
     fetching and parsing a copy of the current page for one span. */
  function refreshBasket() {
    return window.TdhqCart.get().catch(function () { return null; })
      .then(function (cart) {
        if (!cart) return;
        if (window.TdhqQty) { window.TdhqQty.absorb(cart); }
        pending = {};
        var badge = document.querySelector('[data-tdhq-cart-count]');
        if (badge) {
          /* Replace ONLY the leading text node. The badge also contains a
             visually-hidden label, and overwriting textContent would delete it,
             leaving screen readers with a bare number and no context. */
          var first = badge.firstChild;
          if (first && first.nodeType === 3) {
            first.nodeValue = String(cart.item_count) + ' ';
          } else {
            badge.insertBefore(document.createTextNode(String(cart.item_count) + ' '), badge.firstChild);
          }
          var label = badge.querySelector('.tdhq-visually-hidden');
          if (label) {
            label.textContent = cart.item_count === 1 ? 'item in basket' : 'items in basket';
          }
          badge.classList.toggle('tdhq-hidden', cart.item_count === 0);
        }

        /* The header's basket TOTAL.

           This used to be read out of a freshly fetched copy of the whole
           current page: `fetch(location.pathname)` for the sole purpose of
           copying one money string. Measured on the homepage that is 636 KB of
           HTML plus a full DOMParser parse, and the submit handler awaited it
           BEFORE flashing "Added to basket" and before dispatching the event
           that opens the drawer - so the most frequent action on the site sat
           silent for the length of a page load with nothing on screen moving.

           The cart payload already in hand carries `total_price`, and the
           drawer publishes the shop's own money formatter, so the figure is
           written directly. If the drawer script is not on the page the total
           is simply left alone rather than paying for a page fetch: the count
           badge beside it has already updated, so the header is not stale in
           any way a shopper would notice. */
        var total = document.querySelector('[data-tdhq-cart-total]');
        var fmt = window.tdhqFormatMoney;
        if (!fmt) { return; }

        if (total) {
          total.textContent = fmt(cart.total_price);
          return;
        }

        /* NO ELEMENT MEANS THE MERCHANT SAID NO. This used to build one -
           after every add, unconditionally, with a class that has no rule
           anywhere in the theme - so a header configured with "show basket
           value" OFF grew an unstyled price the moment anything was added, and
           kept it for the rest of the visit. The header renders the total when
           it is wanted; when it is not, there is nothing here to update and
           that is the correct outcome. The count badge beside it has already
           updated either way. */
      })
      .catch(function () { /* a stale badge is not worth an error state */ });
  }

  /* A message beside the card that failed, in the same shape both baskets use.
     role="alert" so it is announced as well as shown, and it clears itself:
     a card the shopper has moved past should not keep an old complaint. */
  function showAddError(form, message) {
    if (!form || !message) { return; }
    var card = form.closest('.tdhq-pcard') || form.parentNode;
    /* over the bottom of the picture, in the site's notification style (window.TdhqNote, 23 Oct 2026) */
    var media = card.querySelector('.tdhq-pcard__media');
    if (window.TdhqNote && media) { window.TdhqNote.show(media, message); return; }
    var box = card.querySelector('.tdhq-pcard__adderr');
    if (!box) {
      box = document.createElement('p');
      box.className = 'tdhq-pcard__adderr';
      box.setAttribute('role', 'alert');
      /* inside the form, under the button, so it keeps the form's side padding (23 Oct 2026) */
      form.appendChild(box);
    }
    box.textContent = message;
    if (window.TdhqMotion) { window.TdhqMotion.show(box); }
    window.clearTimeout(box._t);
    box._t = window.setTimeout(function () {
      if (!box.parentNode) { return; }
      if (window.TdhqMotion) { window.TdhqMotion.exit(box, function () { box.remove(); }); } else { box.remove(); }
    }, 8000);
  }

  function flash(button, label) {
    if (!button) return;
    var original = button.getAttribute('data-tdhq-label') || button.textContent.trim();
    button.setAttribute('data-tdhq-label', original);
    button.textContent = label;
    window.setTimeout(function () {
      if (button.getAttribute('data-tdhq-label')) {
        button.textContent = button.getAttribute('data-tdhq-label');
      }
    }, DONE_MS);
  }

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('[data-tdhq-quickadd-btn]') : null;
    /* `form` is the card's add form (form[data-tdhq-quickadd]) */
    var form = button && button.closest('[data-tdhq-quickadd]');
    if (!form) return;
    event.preventDefault();
    if (form.classList.contains(BUSY)) return;

    var variantId = form.getAttribute('data-tdhq-variant-id');
    if (!variantId) return;

    /* NOTHING LEFT TO ADD. The basket already holds every unit in stock:
       say so here instead of posting - Shopify does not always refuse an
       add past the stock (an oversell went through on 23 Sep). */
    var qty = window.TdhqQty;
    if (qty) {
      /* A card drawn after page load (Saved items, recently viewed) has not
         told tdhq-qty its stock yet: its hidden quantity carries it. No max
         means the item is not stock-limited, which retires an old ceiling. */
      var box = form.querySelector('input[name="quantity"]');
      if (box) { qty.remember(variantId, box.getAttribute('max') || 0); }
      if (pending[variantId]) { return; }
      if (qty.headroom(variantId) === 0) {
        showAddError(form, qty.reason(variantId, 'add'));
        return;
      }
      if (qty.stock(variantId) !== null) {
        pending[variantId] = true;
        window.setTimeout(function () { delete pending[variantId]; }, 8000);
      }
    }

    form.classList.add(BUSY);
    if (button) button.disabled = true;

    var kitList = null;
    try { kitList = JSON.parse((form.getAttribute('data-tdhq-kit-items') || '').trim() || 'null'); } catch (err) { kitList = null; }
    if (!Array.isArray(kitList) || !kitList.length) { kitList = null; }

    fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      /* A battery kit built from its parts (data-tdhq-kit-items, snippets/tdhq-kit-items.liquid) adds the parts as
         separate basket lines, each times the quantity, never the kit itself (owner + Simon, 20 Oct 2026). */
      body: JSON.stringify(kitList
        ? { items: kitList.map(function (p) { return { id: p[0], quantity: p[1] }; }), sections: 'tdhq-cart-state', sections_url: window.location.pathname }
        : { id: parseInt(variantId, 10), quantity: 1, sections: 'tdhq-cart-state', sections_url: window.location.pathname })
    })
      .then(function (r) {
        /* Shopify explains its own refusals - "Cannot find variant", "All 3 of
           this item are in your cart" - and that sentence is the only thing
           that tells the shopper why nothing happened. Carry it through as the
           error message instead of throwing a status code away. */
        if (!r.ok) {
          return r.json()
            .catch(function () { throw new Error('Could not add this item. Please try again.'); })
            .then(function (e) { throw new Error(e.description || e.message || 'Could not add this item.'); });
        }
        return r.json();
      })
      .then(function (added) {
        /* Confirm FIRST, reconcile after. The add has already succeeded at this
           point, so the shopper sees the label change and the drawer open in
           the same frame; the header badge and total settle a moment later
           without anything waiting on them. Ordering these the other way round
           is what made a card click feel like it had not registered. */
        form.classList.remove(BUSY);
        if (button) button.disabled = false;
        flash(button, 'Added to basket');
        /* The drawer's own refresh (on tdhq:cart:added below) redraws the
           header count and total; a second /cart.js here only raced it. */
        if (!window.tdhqCartDrawer) { window.setTimeout(refreshBasket, 0); }
        /* Announce it: a visual label change is invisible to a screen reader
           when it happens outside the focused element's own text. */
        /* Hand off to the basket drawer. This script owns ADDING; the drawer
           owns SHOWING. Dispatching instead of calling it directly keeps the two
           independent - with no drawer on the page nothing listens and the flash
           message above remains the confirmation. */
        document.dispatchEvent(new CustomEvent('tdhq:cart:added', { detail: { source: 'quickadd', productId: (form.getAttribute('data-tdhq-product-id') || null), item: added } }));
        var live = document.getElementById('tdhq-quickadd-live');
        if (live) {
          live.textContent = '';
          /* A timer rather than requestAnimationFrame: rAF is paused in a
             background tab, and an announcement that never fires is worse than
             one that fires 30ms late. */
          window.setTimeout(function () {
            live.textContent = (form.getAttribute('data-tdhq-title') || 'Item') + ' added to your basket';
          }, 30);
        }
      })
      .catch(function (err) {
        /* SAY IT HERE. This used to call form.submit(), which posts the real
           /cart/add form as a browser navigation - and a refusal then lands on
           Shopify's own error page ("Cart Error: Cannot find variant",
           "Something went wrong", one link back to the myshopify domain). The
           shopper loses the page they were browsing to be told, badly, that
           one card did not work. Both baskets already answer this class of
           failure in place; so does this now. */
        form.classList.remove(BUSY);
        if (button) button.disabled = false;
        delete pending[variantId];
        showAddError(form, (err && err.message) || 'Could not add this item.');
        /* a refused kit add may still have put some of its parts in the basket */
        if (kitList && window.TdhqQty && window.TdhqQty.reconcile) { window.TdhqQty.reconcile(); }
      });
  }, true);
})();

/* CARD PHOTO FIT moved to snippets/tdhq-fit-script.liquid (2026-09-22): one
   rule for every product picture on the site, not only non-square cards. */
